import { useEffect, useRef, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
    ArrowsClockwise, Sparkle, Printer, ArrowUpRight, Quotes, FileText,
    CaretLeft, CaretRight, Calendar, ChartLine,
} from "@phosphor-icons/react";
import EmptyState from "../components/EmptyState";
import html2pdf from "html2pdf.js";
import { useAuth } from "../lib/auth";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch {
        return "";
    }
}

function shortReading(s) {
    if (!s) return "—";
    const colonSplit = s.split(":");
    if (colonSplit.length > 1) return colonSplit[0].trim().slice(0, 26);
    const periodSplit = s.split(".");
    return periodSplit[0].trim().slice(0, 26);
}

function fmtWeekEnding(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch {
        return "";
    }
}

function MetricRow({ label, reading, interpretation, testid }) {
    return (
        <tr data-testid={testid} className="border-t border-border align-top">
            <td className="py-3.5 pr-4 font-display font-semibold text-[15px] w-[210px] whitespace-nowrap">{label}</td>
            <td className="py-3.5 pr-4 font-mono text-[12px] uppercase tracking-wider w-[180px] whitespace-nowrap">
                {reading}
            </td>
            <td className="py-3.5 editorial text-[14px] text-foreground/80 leading-relaxed">{interpretation}</td>
        </tr>
    );
}

function SectionRule({ title, count, suffix }) {
    return (
        <div className="flex items-baseline gap-3 mb-7">
            <div className="overline">{title}</div>
            <div className="h-px bg-border flex-1" />
            {suffix && <span className="font-mono text-[11px] text-muted-foreground tracking-wider">{suffix}</span>}
            {count != null && (
                <span className="font-mono text-[11px] text-muted-foreground">
                    {String(count).padStart(2, "0")}
                </span>
            )}
        </div>
    );
}

function AggregateView({ data, weeks, archive, onSelectBrief }) {
    if (!data || !data.weeks_returned) {
        return (
            <div className="px-8 lg:px-14 py-16 text-sm text-muted-foreground italic editorial">
                No weekly briefs in the selected window yet.
            </div>
        );
    }
    return (
        <article className="px-8 lg:px-14 py-12 max-w-[1100px] mx-auto space-y-14" data-testid="weekly-aggregate-view">
            <section>
                <SectionRule title={`Rolling Aggregate — Last ${data.weeks_returned} ${data.weeks_returned === 1 ? "week" : "weeks"}`} suffix={`${data.total_signals.toLocaleString()} signals`} />
                <h2 className="font-display font-black text-[clamp(1.6rem,2.5vw,2.2rem)] tracking-tight leading-[1.1] mb-6 max-w-[36ch]">
                    What the last {data.weeks_returned} weeks have been telling us.
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
                    <div>
                        <div className="overline text-[9px] mb-1">Weeks captured</div>
                        <div className="font-display font-black text-3xl tabular-nums">{data.weeks_returned}</div>
                    </div>
                    <div>
                        <div className="overline text-[9px] mb-1">Total signals</div>
                        <div className="font-display font-black text-3xl tabular-nums">{(data.total_signals || 0).toLocaleString()}</div>
                    </div>
                    <div>
                        <div className="overline text-[9px] mb-1">Analyses run</div>
                        <div className="font-display font-black text-3xl tabular-nums">{data.total_analyses || 0}</div>
                    </div>
                    <div>
                        <div className="overline text-[9px] mb-1">Window</div>
                        <div className="font-mono text-[12px] leading-tight">
                            {fmtDate(data.window_start)}<br />→ {fmtDate(data.window_end)}
                        </div>
                    </div>
                </div>
            </section>

            {/* Recurring tensions */}
            {(data.top_tensions || []).length > 0 && (
                <section data-testid="agg-tensions">
                    <SectionRule title="Recurring Tensions" count={data.top_tensions.length} />
                    <ul className="space-y-3 editorial">
                        {data.top_tensions.map((t, i) => (
                            <li key={i} className="border-l-2 border-foreground/30 pl-4 py-1">
                                <div className="text-[15px] font-display font-medium">{t.name}</div>
                                <div className="overline text-[9px] mt-1 text-muted-foreground">
                                    Surfaced in {t.weeks_seen} of {data.weeks_returned} {data.weeks_returned === 1 ? "week" : "weeks"}
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Language drift */}
            {(((data.growing_language || []).length) + ((data.declining_language || []).length) > 0) && (
                <section className="grid md:grid-cols-2 gap-10" data-testid="agg-language">
                    {(data.growing_language || []).length > 0 && (
                        <div>
                            <SectionRule title="Language Growing" count={data.growing_language.length} />
                            <ul className="space-y-1.5">
                                {data.growing_language.map((x, i) => (
                                    <li key={i} className="flex items-baseline justify-between border-b border-border py-1.5">
                                        <span className="editorial text-[14px]">{x.term}</span>
                                        <span className="font-mono text-[10px] text-muted-foreground">{x.weeks_seen}w</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {(data.declining_language || []).length > 0 && (
                        <div>
                            <SectionRule title="Language Declining" count={data.declining_language.length} />
                            <ul className="space-y-1.5">
                                {data.declining_language.map((x, i) => (
                                    <li key={i} className="flex items-baseline justify-between border-b border-border py-1.5">
                                        <span className="editorial text-[14px]">{x.term}</span>
                                        <span className="font-mono text-[10px] text-muted-foreground">{x.weeks_seen}w</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            )}

            {/* Behavioral movement */}
            {(((data.behavioral_increasing || []).length) + ((data.behavioral_decreasing || []).length) > 0) && (
                <section className="grid md:grid-cols-2 gap-10" data-testid="agg-behavior">
                    {(data.behavioral_increasing || []).length > 0 && (
                        <div>
                            <SectionRule title="Behaviors Rising" count={data.behavioral_increasing.length} />
                            <ul className="space-y-1.5">
                                {data.behavioral_increasing.map((x, i) => (
                                    <li key={i} className="flex items-baseline justify-between border-b border-border py-1.5">
                                        <span className="editorial text-[14px]">{x.name}</span>
                                        <span className="font-mono text-[10px] text-muted-foreground">{x.weeks_seen}w</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {(data.behavioral_decreasing || []).length > 0 && (
                        <div>
                            <SectionRule title="Behaviors Fading" count={data.behavioral_decreasing.length} />
                            <ul className="space-y-1.5">
                                {data.behavioral_decreasing.map((x, i) => (
                                    <li key={i} className="flex items-baseline justify-between border-b border-border py-1.5">
                                        <span className="editorial text-[14px]">{x.name}</span>
                                        <span className="font-mono text-[10px] text-muted-foreground">{x.weeks_seen}w</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            )}

            {/* Quietly true */}
            {(data.quietly_true || []).length > 0 && (
                <section data-testid="agg-quietly">
                    <SectionRule title="Quietly True" count={data.quietly_true.length} />
                    <ul className="space-y-3 editorial">
                        {data.quietly_true.map((q, i) => (
                            <li key={i} className="border-l-2 border-foreground/30 pl-4 py-1">
                                <div className="text-[15px] italic">"{q.text}"</div>
                                <div className="overline text-[9px] mt-1 text-muted-foreground">{q.weeks_seen} of {data.weeks_returned}w</div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Platform themes */}
            {(data.platform_themes || []).length > 0 && (
                <section data-testid="agg-platforms">
                    <SectionRule title="Platform Themes" count={data.platform_themes.length} />
                    <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
                        {data.platform_themes.map((p, i) => (
                            <div key={i} className="border-b border-border py-2 flex items-baseline justify-between gap-3">
                                <div>
                                    <div className="overline text-[9px] text-muted-foreground">{p.platform}</div>
                                    <div className="text-[13px] editorial leading-snug">{p.theme}</div>
                                </div>
                                <span className="font-mono text-[10px] text-muted-foreground shrink-0">{p.weeks_seen}w</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Per-week timeline */}
            <section data-testid="agg-history">
                <SectionRule title="Brief History" count={data.history?.length} />
                <ul className="space-y-2.5">
                    {(data.history || []).map((h, i) => (
                        <li key={h.id || i} className="border border-border bg-card rounded-sm p-4 flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="overline text-[9px]">Week ending</span>
                                    <span className="font-mono text-[11px]">{fmtDate(h.week_ending || h.generated_at)}</span>
                                    {i === 0 && <span className="font-mono text-[9px] text-primary border border-primary/30 px-1.5 py-0.5 rounded-sm">CURRENT</span>}
                                </div>
                                <div className="font-display font-semibold text-[14px] mb-1 leading-tight">{h.headline}</div>
                                <div className="editorial text-[13px] text-foreground/80 leading-snug line-clamp-2">{h.executive_signal}</div>
                            </div>
                            <Button
                                data-testid={`agg-open-${h.id}`}
                                variant="ghost" size="sm"
                                className="rounded-sm shrink-0"
                                onClick={() => onSelectBrief(h.id)}
                            >
                                Open <ArrowUpRight size={11} className="ml-1" />
                            </Button>
                        </li>
                    ))}
                </ul>
            </section>
        </article>
    );
}

export default function WeeklyBrief() {
    const { isAdmin } = useAuth();
    const [brief, setBrief] = useState(null);
    const [archive, setArchive] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);
    const docRef = useRef(null);
    // View mode: "single" (a specific brief) | "aggregate" (rolling-window).
    const [viewMode, setViewMode] = useState("single");
    const [selectedBriefId, setSelectedBriefId] = useState(null);
    const [aggregateWeeks, setAggregateWeeks] = useState(4);
    const [aggregate, setAggregate] = useState(null);
    const [switching, setSwitching] = useState(false);

    const archiveById = useMemo(() => {
        const m = new Map();
        archive.forEach((b) => m.set(b.id, b));
        return m;
    }, [archive]);

    const load = async () => {
        const [b, a] = await Promise.all([
            api.get("/dashboard/weekly-brief").then((r) => r.data).catch(() => null),
            api.get("/dashboard/weekly-brief/archive?limit=24").then((r) => r.data?.briefs || []).catch(() => []),
        ]);
        const latest = b && b.headline ? b : null;
        setBrief(latest);
        setArchive(a);
        setSelectedBriefId(latest?.id || a?.[0]?.id || null);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    // Swap brief when the user picks a historical one.
    const selectBrief = async (briefId) => {
        if (!briefId || briefId === brief?.id) {
            setSelectedBriefId(briefId);
            setViewMode("single");
            return;
        }
        setSwitching(true);
        try {
            const r = await api.get(`/dashboard/weekly-brief/by-id/${briefId}`);
            setBrief(r.data);
            setSelectedBriefId(briefId);
            setViewMode("single");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load brief");
        } finally {
            setSwitching(false);
        }
    };

    // Step prev/next through the archive (sorted desc — index 0 = latest).
    const stepBrief = async (direction) => {
        if (!archive.length) return;
        const currentIndex = archive.findIndex((b) => b.id === (selectedBriefId || brief?.id));
        const nextIndex = direction === "older"
            ? Math.min(archive.length - 1, (currentIndex < 0 ? 0 : currentIndex) + 1)
            : Math.max(0, (currentIndex < 0 ? 0 : currentIndex) - 1);
        if (archive[nextIndex] && archive[nextIndex].id !== (selectedBriefId || brief?.id)) {
            await selectBrief(archive[nextIndex].id);
        }
    };

    // Load the rolling-window aggregate.
    const loadAggregate = async (weeks) => {
        const w = weeks || aggregateWeeks;
        setSwitching(true);
        try {
            const r = await api.get(`/dashboard/weekly-brief/aggregate?weeks=${w}`);
            setAggregate(r.data || null);
            setAggregateWeeks(w);
            setViewMode("aggregate");
        } catch (e) {
            toast.error("Failed to load aggregate");
        } finally {
            setSwitching(false);
        }
    };

    const generate = async () => {
        setGenerating(true);
        const tid = toast.loading("Synthesizing weekly intelligence brief…");
        try {
            const r = await api.post("/dashboard/weekly-brief", { since_days: 7 });
            setBrief(r.data);
            toast.success("Brief synthesized", { id: tid });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Synthesis failed", { id: tid });
        } finally {
            setGenerating(false);
        }
    };

    const exportPDF = async () => {
        if (!docRef.current) return;
        setExporting(true);
        const tid = toast.loading("Building PDF…");
        try {
            // Reveal the print-only document header (logo + title) before render.
            docRef.current.classList.add("pdf-mode");
            const filename = `HAEVN-Labs-Weekly-Brief-${(brief?.week_ending || "").slice(0, 10)}.pdf`;
            await html2pdf()
                .set({
                    margin: [0.6, 0.6, 0.6, 0.6],
                    filename,
                    image: { type: "jpeg", quality: 0.95 },
                    html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
                    jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
                    pagebreak: { mode: ["css", "legacy"], avoid: ["tr", "li"] },
                })
                .from(docRef.current)
                .save();
            toast.success("PDF downloaded", { id: tid });
        } catch (e) {
            toast.error("PDF export failed — try again", { id: tid });
            // eslint-disable-next-line no-console
            console.error("PDF export error:", e);
        } finally {
            docRef.current?.classList.remove("pdf-mode");
            setExporting(false);
        }
    };

    if (loading) {
        return <div className="px-8 lg:px-14 pt-16 text-sm text-muted-foreground">Loading brief…</div>;
    }

    if (!brief) {
        return (
            <>
                <header className="px-8 lg:px-14 pt-12 pb-6 border-b border-border">
                    <div className="overline mb-3">HAEVN Labs</div>
                    <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Weekly Intelligence Brief</h1>
                    <p className="editorial text-muted-foreground mt-2 max-w-[68ch]">
                        A long-form synthesis of cultural signals captured across all narrative streams in the last 7 days.
                    </p>
                </header>
                <div className="px-8 lg:px-14 py-12">
                    <EmptyState
                        title="No brief generated yet"
                        description={isAdmin
                            ? "Once at least one stream has fresh analyses, click below to synthesize the first weekly brief. Briefs auto-generate weekly thereafter."
                            : "An administrator hasn't generated a weekly brief yet. Once they do, it will appear here."}
                        action={isAdmin ? (
                            <Button data-testid="generate-weekly-btn" onClick={generate} disabled={generating} className="rounded-sm">
                                {generating ? <ArrowsClockwise size={14} className="mr-2 animate-spin" /> : <Sparkle size={14} weight="duotone" className="mr-2" />}
                                {generating ? "Synthesizing…" : "Generate first brief"}
                            </Button>
                        ) : null}
                        testid="weekly-brief-empty"
                    />
                </div>
            </>
        );
    }

    const m = brief.metrics || {};
    const snap = brief.snapshot_interpretations || {};
    const platformThemes = brief.platform_themes || [];
    const xpoll = brief.cross_platform_recurrence || [];
    const tensions = brief.tensions || [];
    const quietly = brief.quietly_true || [];
    const growing = brief.growing_language || [];
    const declining = brief.declining_language || [];
    const incBeh = brief.behavioral_increasing || [];
    const decBeh = brief.behavioral_decreasing || [];
    const strat = brief.strategic_interpretation || {};
    const haevn = brief.haevn_implications || {};
    const forecast = brief.forecast || {};
    const founderPrompts = brief.founder_prompts || [];

    return (
        <>
            {/* PRINT/PDF STYLES — show document header only inside the captured PDF area */}
            <style>{`
                .pdf-doc-header { display: none; }
                .pdf-mode .pdf-doc-header { display: block; }
                .pdf-mode [data-testid="weekly-actions"],
                .pdf-mode [data-testid="weekly-archive"] { display: none !important; }
                @media print {
                    body { background: #fff !important; color: #000 !important; }
                    [data-testid="app-sidebar"],
                    [data-testid="weekly-actions"],
                    [data-testid="weekly-archive"] { display: none !important; }
                    .pdf-doc-header { display: block !important; }
                    @page { size: letter; margin: 0.6in 0.7in; }
                    article { max-width: 100% !important; }
                    .page-break { page-break-before: always; }
                }
            `}</style>

            <header data-testid="weekly-header" className="px-8 lg:px-14 pt-14 pb-8 border-b border-border">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div>
                        <div className="overline mb-3 text-primary">HAEVN Labs</div>
                        <h1 className="font-display font-black tracking-[-0.025em] leading-[0.95] text-[clamp(2rem,4vw,3.6rem)]">
                            Weekly Intelligence Brief
                        </h1>
                        <p className="font-mono text-[12px] text-muted-foreground mt-3 tracking-wider">
                            Week ending — {fmtWeekEnding(brief.week_ending)}
                            <span className="mx-2 text-border">·</span>
                            All Streams · Global
                            <span className="mx-2 text-border">·</span>
                            {(m.total_signals || 0).toLocaleString()} signals
                        </p>
                    </div>
                    <div data-testid="weekly-actions" className="flex gap-2 items-center flex-wrap">
                        {/* Brief picker — Current / Specific past brief / Aggregate window */}
                        {archive.length > 0 && viewMode === "single" && (
                            <div className="flex items-center gap-1.5 mr-1">
                                <Button
                                    data-testid="weekly-prev-btn"
                                    variant="ghost" size="sm"
                                    className="rounded-sm h-8 w-8 p-0"
                                    onClick={() => stepBrief("older")}
                                    disabled={switching}
                                    title="Previous week"
                                >
                                    <CaretLeft size={13} />
                                </Button>
                                <Select
                                    value={selectedBriefId || ""}
                                    onValueChange={selectBrief}
                                    data-testid="weekly-picker"
                                >
                                    <SelectTrigger className="rounded-sm h-8 w-[230px] text-xs">
                                        <Calendar size={12} className="mr-1.5 text-muted-foreground" />
                                        <SelectValue placeholder="Select a brief" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {archive.map((b, idx) => (
                                            <SelectItem key={b.id} value={b.id}>
                                                {idx === 0 ? "Current — " : ""}
                                                {fmtDate(b.week_ending || b.generated_at)}
                                                {idx === 1 ? "  (Prior week)" : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button
                                    data-testid="weekly-next-btn"
                                    variant="ghost" size="sm"
                                    className="rounded-sm h-8 w-8 p-0"
                                    onClick={() => stepBrief("newer")}
                                    disabled={switching}
                                    title="Next week"
                                >
                                    <CaretRight size={13} />
                                </Button>
                            </div>
                        )}

                        {/* Aggregate window selector */}
                        {archive.length > 1 && (
                            <div className="flex items-center gap-1 mr-1 border border-border rounded-sm h-8 px-1.5" data-testid="weekly-aggregate-toggle">
                                <ChartLine size={12} className="text-muted-foreground mx-1" />
                                <span className="overline text-[9px] mr-1">Aggregate</span>
                                {[2, 3, 4].map((w) => (
                                    <Button
                                        key={w}
                                        data-testid={`weekly-aggregate-${w}w`}
                                        variant={viewMode === "aggregate" && aggregateWeeks === w ? "default" : "ghost"}
                                        size="sm"
                                        className="rounded-sm h-6 px-2 text-[10px] font-mono"
                                        onClick={() => loadAggregate(w)}
                                        disabled={switching || archive.length < w}
                                        title={`Roll up the last ${w} weeks into one view`}
                                    >
                                        {w}w
                                    </Button>
                                ))}
                                {viewMode === "aggregate" && (
                                    <Button
                                        data-testid="weekly-aggregate-close"
                                        variant="ghost" size="sm"
                                        className="rounded-sm h-6 px-2 text-[10px] font-mono text-muted-foreground"
                                        onClick={() => { setViewMode("single"); setAggregate(null); }}
                                        title="Back to single brief"
                                    >
                                        Single
                                    </Button>
                                )}
                            </div>
                        )}

                        {isAdmin && viewMode === "single" && (
                            <Button data-testid="weekly-regen-btn" variant="outline" size="sm" onClick={generate} disabled={generating} className="rounded-sm">
                                {generating ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" /> : <ArrowsClockwise size={13} className="mr-1.5" />}
                                Re-synthesize
                            </Button>
                        )}
                        {viewMode === "single" && (
                            <Button data-testid="weekly-print-btn" size="sm" onClick={exportPDF} disabled={exporting} className="rounded-sm">
                                {exporting ? <ArrowsClockwise size={13} className="mr-1.5 animate-spin" /> : <Printer size={13} className="mr-1.5" />}
                                {exporting ? "Building PDF…" : "Download PDF"}
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            {viewMode === "aggregate" ? (
                <AggregateView
                    data={aggregate}
                    weeks={aggregateWeeks}
                    archive={archive}
                    onSelectBrief={selectBrief}
                />
            ) : (
            <article ref={docRef} className="px-8 lg:px-14 py-12 max-w-[1100px] mx-auto space-y-16">

                {/* PDF-ONLY DOCUMENT HEADER — only visible when exporting/printing */}
                <div className="pdf-doc-header pb-6 mb-4 border-b-2 border-foreground" data-testid="pdf-doc-header">
                    {/* Small logo strip — top left, out of the way */}
                    <div className="flex items-center gap-2 mb-6">
                        <img src="/haevn-mark.png" alt="HAEVN" style={{ height: 18, width: "auto" }} />
                        <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-muted-foreground">HAEVN Labs</span>
                    </div>
                    {/* Title row */}
                    <div className="flex items-end justify-between gap-6">
                        <div>
                            <div className="font-display font-black text-[28px] tracking-tight leading-tight">Weekly Intelligence Brief</div>
                        </div>
                        <div className="text-right">
                            <div className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">Week ending</div>
                            <div className="font-display font-semibold text-[15px]">{fmtWeekEnding(brief.week_ending)}</div>
                            <div className="font-mono text-[10px] tracking-wider text-muted-foreground mt-2">
                                {(m.total_signals || 0).toLocaleString()} signals · All Streams · Global
                            </div>
                        </div>
                    </div>
                </div>

                {/* EXECUTIVE SIGNAL */}
                <section data-testid="exec-signal">
                    <SectionRule title="Executive Signal" />
                    <h2 className="font-display font-bold text-[clamp(1.6rem,2.5vw,2.4rem)] tracking-tight leading-[1.1] mb-6 max-w-[28ch]">
                        {brief.headline}
                    </h2>
                    <div className="editorial-lead text-[17px] md:text-[18px] text-foreground/90 max-w-[68ch] whitespace-pre-line leading-[1.6]">
                        {brief.executive_signal}
                    </div>
                </section>

                {/* SIGNAL INTELLIGENCE SNAPSHOT */}
                <section data-testid="snapshot">
                    <SectionRule title="Signal Intelligence Snapshot" />
                    <table className="w-full border-t border-border">
                        <thead>
                            <tr className="text-left">
                                <th className="overline text-[9px] py-2 pr-4 w-[210px]">Metric</th>
                                <th className="overline text-[9px] py-2 pr-4 w-[180px]">Current Reading</th>
                                <th className="overline text-[9px] py-2">Interpretation</th>
                            </tr>
                        </thead>
                        <tbody>
                            <MetricRow testid="metric-total" label="Total Signals" reading={(m.total_signals || 0).toLocaleString()} interpretation={snap.total_signals} />
                            <MetricRow testid="metric-resonance" label="Resonance Level" reading={m.resonance_label} interpretation={snap.resonance} />
                            <MetricRow testid="metric-velocity" label="Velocity" reading={m.velocity_label} interpretation={snap.velocity} />
                            <MetricRow testid="metric-persistence" label="Persistence" reading={m.persistence_label} interpretation={snap.persistence} />
                            <MetricRow testid="metric-density" label="Cross-Platform Density" reading={m.density_label} interpretation={snap.cross_platform_density} />
                            <MetricRow testid="metric-intensity" label="Emotional Intensity" reading={shortReading(snap.emotional_intensity)} interpretation={snap.emotional_intensity} />
                            <MetricRow testid="metric-maturity" label="Cultural Maturity" reading={shortReading(snap.cultural_maturity)} interpretation={snap.cultural_maturity} />
                        </tbody>
                    </table>
                </section>

                {/* CROSS-PLATFORM SIGNAL STRENGTH */}
                {platformThemes.length > 0 && (
                    <section data-testid="platform-strength">
                        <SectionRule title="Cross-Platform Signal Strength" />
                        <table className="w-full border-t border-border">
                            <thead>
                                <tr className="text-left">
                                    <th className="overline text-[9px] py-2 pr-4 w-[140px]">Platform</th>
                                    <th className="overline text-[9px] py-2 pr-4 w-[140px]">Signals Detected</th>
                                    <th className="overline text-[9px] py-2 pr-4 w-[140px]">Resonance</th>
                                    <th className="overline text-[9px] py-2">Primary Behavioral Theme</th>
                                </tr>
                            </thead>
                            <tbody>
                                {platformThemes.map((pt, i) => {
                                    const count = (m.platform_counts || {})[pt.platform] || 0;
                                    const resLabel = (m.platform_resonance_labels || {})[pt.platform] || "Low";
                                    return (
                                        <tr key={i} className="border-t border-border align-top" data-testid={`platform-row-${pt.platform}`}>
                                            <td className="py-3.5 pr-4 font-display font-semibold text-[15px]">{pt.platform}</td>
                                            <td className="py-3.5 pr-4 font-mono text-[13px] tabular-nums">{count.toLocaleString()}</td>
                                            <td className="py-3.5 pr-4 font-mono text-[12px] uppercase tracking-wider">{resLabel}</td>
                                            <td className="py-3.5 editorial text-[14px] text-foreground/80">{pt.primary_behavioral_theme}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </section>
                )}

                {/* CORE TENSIONS */}
                {tensions.length > 0 && (
                    <section data-testid="weekly-tensions" className="page-break">
                        <SectionRule title="Core Tensions Emerging This Week" count={tensions.length} />
                        <div className="space-y-9">
                            {tensions.map((t, i) => {
                                const text = typeof t === "string" ? t : t.tension;
                                const split = text && text.match(/^(.+?)\s+\/?\s*but\s*\/?\s+(.+)$/i);
                                return (
                                    <div key={i} className="border-l-2 border-accent pl-6">
                                        <div className="overline text-[10px] mb-2">Tension · {String(i + 1).padStart(2, "0")}</div>
                                        {split ? (
                                            <p className="editorial text-[20px] leading-snug mb-3">
                                                <span>{split[1].trim()}</span>
                                                <span className="text-accent italic mx-2">/ but /</span>
                                                <span>{split[2].trim()}</span>
                                            </p>
                                        ) : (
                                            <p className="editorial text-[20px] leading-snug mb-3">{text}</p>
                                        )}
                                        {t.platforms && t.platforms.length > 0 && (
                                            <div className="font-mono text-[11px] text-muted-foreground mb-1">
                                                Observed across: {t.platforms.join(", ")}
                                            </div>
                                        )}
                                        {t.recurring_themes && t.recurring_themes.length > 0 && (
                                            <div className="font-mono text-[11px] text-muted-foreground mb-3">
                                                Recurring themes: {t.recurring_themes.join(", ")}
                                            </div>
                                        )}
                                        {t.interpretation && (
                                            <p className="editorial text-[15px] text-foreground/80 leading-relaxed max-w-[60ch]">
                                                {t.interpretation}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* WHAT'S QUIETLY TRUE */}
                {quietly.length > 0 && (
                    <section data-testid="weekly-quietly">
                        <SectionRule title="What's Quietly True" count={quietly.length} />
                        <div className="space-y-10 max-w-[68ch]">
                            {quietly.map((o, i) => (
                                <div key={i}>
                                    <div className="font-mono text-[11px] text-primary tabular-nums mb-2">№ {String(i + 1).padStart(2, "0")}</div>
                                    <h3 className="font-display font-bold text-[20px] tracking-tight leading-[1.2] mb-3">{o.observation}</h3>
                                    {o.elaboration && (
                                        <p className="editorial text-[15px] text-foreground/80 leading-relaxed">{o.elaboration}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* EMERGING VOCABULARY */}
                {(growing.length > 0 || declining.length > 0) && (
                    <section data-testid="weekly-vocabulary">
                        <SectionRule title="Emerging Vocabulary" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            {growing.length > 0 && (
                                <div>
                                    <div className="overline text-[10px] mb-3">Growing Language Signals</div>
                                    <ul className="flex flex-wrap gap-x-4 gap-y-2">
                                        {growing.map((r, i) => (
                                            <li key={i} className="font-display font-medium text-[15px] border-b border-border pb-0.5">{r}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {declining.length > 0 && (
                                <div>
                                    <div className="overline text-[10px] mb-3 text-muted-foreground">Declining Resonance</div>
                                    <ul className="flex flex-wrap gap-x-4 gap-y-2">
                                        {declining.map((r, i) => (
                                            <li key={i} className="font-display font-medium text-[15px] text-muted-foreground line-through decoration-1 decoration-muted-foreground/40">{r}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* BEHAVIORAL PATTERNS */}
                {(incBeh.length > 0 || decBeh.length > 0) && (
                    <section data-testid="weekly-behavior">
                        <SectionRule title="Behavioral Patterns Observed" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            {incBeh.length > 0 && (
                                <div>
                                    <div className="overline text-[10px] mb-3 text-emerald-600">Increasing</div>
                                    <ul className="space-y-2">
                                        {incBeh.map((b, i) => (
                                            <li key={i} className="editorial text-[15px] leading-snug">
                                                <span className="font-mono text-[10px] text-muted-foreground mr-2">↑</span>{b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {decBeh.length > 0 && (
                                <div>
                                    <div className="overline text-[10px] mb-3 text-accent">Decreasing</div>
                                    <ul className="space-y-2">
                                        {decBeh.map((b, i) => (
                                            <li key={i} className="editorial text-[15px] leading-snug">
                                                <span className="font-mono text-[10px] text-muted-foreground mr-2">↓</span>{b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* CROSS-PLATFORM RECURRENCE BY EMOTION */}
                {xpoll.length > 0 && (
                    <section data-testid="weekly-xpoll">
                        <SectionRule title="Cross-Platform Recurrence" suffix="emotional themes" />
                        <div className="space-y-4">
                            {xpoll.map((row, i) => (
                                <div key={i} className="grid grid-cols-12 gap-4 border-b border-border pb-3 last:border-b-0">
                                    <div className="col-span-12 md:col-span-4 font-display font-semibold text-[16px] capitalize">{row.theme}</div>
                                    <div className="col-span-12 md:col-span-4 font-mono text-[11px] text-muted-foreground">
                                        Observed across: {row.platforms.join(", ")}
                                    </div>
                                    <div className="col-span-12 md:col-span-4 font-mono text-[11px] text-muted-foreground md:text-right">
                                        {row.recurrence}× recurrence
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* STRATEGIC INTERPRETATION */}
                {strat.narrative && (
                    <section data-testid="weekly-strategic" className="page-break">
                        <SectionRule title="Strategic Interpretation" />
                        <div className="grid grid-cols-12 gap-6 mb-6">
                            <div className="col-span-12 md:col-span-5 border border-border bg-card p-5">
                                <div className="overline text-[10px] mb-2 text-muted-foreground">From</div>
                                <h3 className="font-display font-bold text-[22px] tracking-tight">{strat.from_paradigm}</h3>
                            </div>
                            <div className="col-span-12 md:col-span-2 flex items-center justify-center">
                                <span className="text-3xl text-accent">→</span>
                            </div>
                            <div className="col-span-12 md:col-span-5 border border-accent bg-accent/[0.04] p-5">
                                <div className="overline text-[10px] mb-2 text-accent">Toward</div>
                                <h3 className="font-display font-bold text-[22px] tracking-tight">{strat.to_paradigm}</h3>
                            </div>
                        </div>
                        <p className="editorial-lead text-[17px] text-foreground/90 leading-[1.6] max-w-[68ch] whitespace-pre-line">
                            {strat.narrative}
                        </p>
                    </section>
                )}

                {/* IMPLICATIONS FOR HAEVN */}
                {(haevn.messaging_themes?.length || haevn.product_validation_signals?.length) && (
                    <section data-testid="weekly-haevn">
                        <SectionRule title="Implications for HAEVN" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div>
                                <div className="overline text-[10px] mb-3">Messaging Themes</div>
                                <ul className="space-y-1.5 mb-6">
                                    {(haevn.messaging_themes || []).map((m, i) => (
                                        <li key={i} className="editorial text-[15px]">
                                            <span className="font-mono text-[10px] text-muted-foreground mr-2 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                                            {m}
                                        </li>
                                    ))}
                                </ul>
                                {(haevn.messaging_directions || []).length > 0 && (
                                    <>
                                        <div className="overline text-[10px] mb-3">Potential Messaging</div>
                                        <ul className="space-y-3">
                                            {haevn.messaging_directions.map((d, i) => (
                                                <li key={i} className="relative pl-7">
                                                    <Quotes size={16} weight="fill" className="absolute left-0 top-1 text-primary/30" />
                                                    <span className="editorial italic text-[16px] leading-snug">{d.replace(/^["']|["']$/g, "")}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                            <div>
                                <div className="overline text-[10px] mb-3">Product Validation Signals</div>
                                <ul className="space-y-2">
                                    {(haevn.product_validation_signals || []).map((p, i) => (
                                        <li key={i} className="editorial text-[15px] leading-snug border-l-2 border-primary/40 pl-4">{p}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>
                )}

                {/* FORECAST */}
                {(forecast.short_term || forecast.mid_term) && (
                    <section data-testid="weekly-forecast">
                        <SectionRule title="Forecast" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            {forecast.short_term && (
                                <div>
                                    <div className="overline text-[10px] mb-3 text-primary">Short-Term · 3–6 months</div>
                                    <p className="editorial text-[15px] leading-relaxed text-foreground/90 whitespace-pre-line">{forecast.short_term}</p>
                                </div>
                            )}
                            {forecast.mid_term && (
                                <div>
                                    <div className="overline text-[10px] mb-3 text-primary">Mid-Term · 12–24 months</div>
                                    <p className="editorial text-[15px] leading-relaxed text-foreground/90 whitespace-pre-line">{forecast.mid_term}</p>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* FOR THE FOUNDER CAMERA */}
                {founderPrompts.length > 0 && (
                    <section data-testid="weekly-founder">
                        <SectionRule title="For the Founder Camera" suffix="discussion prompts" />
                        <ol className="space-y-4 max-w-[60ch]">
                            {founderPrompts.map((p, i) => (
                                <li key={i} className="flex gap-4 items-start">
                                    <span className="font-mono text-[11px] text-primary tabular-nums pt-1">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="editorial text-[16px] leading-snug">{p}</span>
                                </li>
                            ))}
                        </ol>
                    </section>
                )}

                {/* FINAL TAKEAWAY */}
                {brief.final_takeaway && (
                    <section data-testid="weekly-takeaway">
                        <SectionRule title="Final Takeaway" />
                        <div className="border-t-2 border-foreground pt-7 max-w-[68ch]">
                            <p className="editorial-lead text-[19px] text-foreground leading-[1.55] whitespace-pre-line">
                                {brief.final_takeaway}
                            </p>
                        </div>
                    </section>
                )}

                {/* ARCHIVE */}
                {archive.length > 1 && (
                    <section data-testid="weekly-archive" className="mt-20 pt-8 border-t border-border">
                        <div className="overline mb-4">Past briefs</div>
                        <ul className="space-y-2">
                            {archive.slice(1, 13).map((a) => (
                                <li key={a.id} className="flex items-baseline gap-3 text-[13px]">
                                    <FileText size={11} className="text-muted-foreground" />
                                    <span className="font-mono text-[11px] text-muted-foreground tracking-wider">
                                        {fmtWeekEnding(a.week_ending)}
                                    </span>
                                    <span className="editorial text-foreground/80 line-clamp-1">{a.headline}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Footer */}
                <footer className="mt-20 pt-6 border-t border-border text-center">
                    <div className="overline mb-1">HAEVN Labs</div>
                    <div className="font-mono text-[10px] text-muted-foreground tracking-wider">
                        Relationship Intelligence for the Modern Era · Synthesized {fmtDate(brief.generated_at)} · {brief.model_used}
                    </div>
                    <Link to="/" className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-primary mt-3 inline-flex items-center gap-1">
                        Back to briefing <ArrowUpRight size={11} />
                    </Link>
                </footer>
            </article>
            )}
        </>
    );
}
